import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import _noop from "lodash/noop";

import { DownloadApps } from "../../components/DownloadApp/DownloadApp";
import { FirewallSwitcher } from "./components/FirewallSwitcher/FirewallSwitcher";
import { selectCurrentCustomerSubscription } from "../../store/subscriptions";
import {
  fetchOptionsTrigger,
  selectOptionsDataFirewall,
  selectOptionsEntitlements,
  selectOptionsIsFetching,
  updateOptionsTrigger,
} from "../../store/account";
import { FEATURE_CODE } from "../../utils/common/contants";
import { selectCustomerData } from "../../store/user";
import { getServiceCodeByTenant } from "../../utils/tenant";
import { useDialog } from "../../hooks";
import { AxiosError } from "axios";
import { checkIsETagError } from "../../utils/common";
import Toaster from "../../services/toaster";
import { ConfirmModal } from "../../components/ConfirmModal/ConfirmModal";

export const Services: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [
    isShowFirewallConfirmationModal,
    openConfirmationModal,
    closeConfirmationModal,
  ] = useDialog();

  const isFirewallActive = useSelector(selectOptionsDataFirewall);
  const isFetchingOptions = useSelector(selectOptionsIsFetching);
  const customer = useSelector(selectCustomerData);
  const subscription = useSelector(selectCurrentCustomerSubscription);
  const optionsEntitlements = useSelector(selectOptionsEntitlements);
  const productCode = useMemo(() => {
    return subscription?.sku?.product_line_code
      ? subscription.sku.product_line_code.toLowerCase()
      : "";
  }, [subscription]);
  const serviceCode = getServiceCodeByTenant(customer?.tenant_code);

  useEffect(() => {
    dispatch(fetchOptionsTrigger(serviceCode));
  }, [dispatch, serviceCode]);

  const isFirewallAvailable = useMemo(() => {
    const isSubscriptionHasFirewall = subscription?.sku?.product.entitlements.some(
      (entitlement) => {
        return entitlement.feature_code === FEATURE_CODE.FEATURE_NATFIREWALL;
      },
    );
    const isOptionsHaveFirewall = optionsEntitlements?.includes(
      FEATURE_CODE.FEATURE_NATFIREWALL,
    );

    return !!(isSubscriptionHasFirewall && isOptionsHaveFirewall);
  }, [subscription, optionsEntitlements]);

  const confirmationText = useMemo(() => {
    return isFirewallActive ? t("disableFirewall") : t("enableFirewall");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirewallActive]);

  const onDisableError = useCallback(
    (error: AxiosError) => {
      if (checkIsETagError(error.response?.data.error_code)) {
        Toaster.error({
          content: t(`error.${error.response?.data.error_code}`),
        });
        return;
      }

      Toaster.error({ content: t("somethingWentWrong") });
    },
    [t],
  );

  const onConfirm = () => {
    try {
      dispatch(
        updateOptionsTrigger({
          onSuccess: _noop,
          onError: onDisableError,
          serviceCode,
          firewall: !isFirewallActive,
        }),
      );
    } finally {
      closeConfirmationModal();
    }
  };

  return (
    <section className="ams-services">
      <h1 className="ams-services__header">
        {t("manageServices", { productCode: t(productCode) })}
      </h1>

      {isFirewallAvailable && (
        <FirewallSwitcher onClick={openConfirmationModal} />
      )}

      <DownloadApps />

      {isShowFirewallConfirmationModal ? (
        <ConfirmModal
          title={confirmationText}
          message={
            isFirewallActive
              ? t("recommendEnableFirewallInstallFirewall")
              : t("recommendEnableFirewall")
          }
          submitBtnText={confirmationText}
          isLoading={isFetchingOptions}
          isOpen={isShowFirewallConfirmationModal}
          onSubmit={onConfirm}
          onDismiss={closeConfirmationModal}
          className={"ams-firewall-confirmation-modal"}
        />
      ) : null}
    </section>
  );
};
